/**
 * Design system component: BottomNav
 *
 * Fixed bottom navigation bar for mobile viewports (below 768px).
 * Rendered on all protected screens via Layout.tsx.
 *
 * Three destinations per §2.7:
 *   - Groups  → /dashboard (also active for /groups/*)
 *   - My Items → /my-items
 *   - Account  → /account
 *
 * Styling: frosted glass (rgba white + blur), border-top, safe-area-inset
 * padding for devices with home indicators. Active item uses accent colour
 * with bold label; inactive uses muted colour.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Package, User } from 'lucide-react';
import { T, BREAKPOINT, CONTAINER, SPACE } from '@/lib/tokens';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Groups', icon: LayoutGrid },
  { path: '/my-items', label: 'My Items', icon: Package },
  { path: '/account', label: 'Account', icon: User },
] as const;

/**
 *
 * @param path
 * @param pathname
 */
function isItemActive(path: string, pathname: string): boolean {
  if (path === '/dashboard') {
    return pathname === '/dashboard' || pathname.startsWith('/groups/');
  }
  return pathname === path;
}

/**
 *
 */
export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobileNav, setIsMobileNav] = useState(() => window.innerWidth < BREAKPOINT.nav);

  useEffect(() => {
    const handler = () => setIsMobileNav(window.innerWidth < BREAKPOINT.nav);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  if (!isMobileNav) return null;

  return (
    <nav
      aria-label="Main navigation"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        margin: '0 auto',
        maxWidth: CONTAINER.mobile,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderTop: `1px solid ${T.border}`,
        display: 'flex',
        zIndex: 50,
        // Safe area inset for devices with home bar
        paddingTop: 10,
        paddingBottom: 'max(22px, env(safe-area-inset-bottom))',
      }}
    >
      {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
        const active = isItemActive(path, location.pathname);
        return (
          <button
            key={path}
            onClick={() => navigate(path)}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              minHeight: SPACE.minTapTarget,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: active ? T.accent : T.textMuted,
            }}
          >
            <Icon size={22} strokeWidth={active ? 2.2 : 1.8} />
            <span
              style={{
                fontSize: 10,
                fontWeight: active ? 700 : 400,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                lineHeight: 1,
              }}
            >
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
